'use client';

import Pagination from '@/components/pagination';
import Table from '@/components/table';
import { useMediaQuery } from '@/hooks/useMediaQuery.hook';
import AlertBar from './alert-bar';
import Description from './description';
import DownloadButton from './download-button';
import NoResultLabel from './no-result-label';
import {
  ColumnRenderInfoList,
  TABLE_OFFSET_TOP,
  TABLE_OFFSET_TOP_MOBILE,
} from '../_const';
import { useAlertVisible, usePagination, useScrollToElement } from '../_hooks';
import { TableDataType } from '../_types';
import {
  getInvalidGeneAlertBarProps,
  getPrefixMatchAlertBarProps,
} from '../_utils';

interface SearchResultProps {
  searchFilteredGeneData: TableDataType[];
  searchedInvalidGenes: string[];
  prefixMatchedGenes: string[];
  searchedPrefixes: string[];
}

function SearchResult({
  searchFilteredGeneData,
  searchedInvalidGenes,
  prefixMatchedGenes,
  searchedPrefixes,
}: SearchResultProps) {
  const isMobile = useMediaQuery('(max-width: 768px)');

  const { ref: contentRef, scrollToElement: scrollToContent } =
    useScrollToElement<HTMLDivElement>(
      isMobile ? TABLE_OFFSET_TOP_MOBILE : TABLE_OFFSET_TOP,
    );

  const {
    currentPage,
    jump,
    prev,
    next,
    totalPages,
    currentPageData,
    currentPageEndNumber,
    currentPageStartNumber,
  } = usePagination(searchFilteredGeneData, {
    onPageChange: scrollToContent,
  });

  const {
    isAlertVisible: isInvalidGeneAlertVisible,
    closeAlertBar: closeInvalidGeneAlertBar,
  } = useAlertVisible(searchedInvalidGenes);

  const {
    isAlertVisible: isPrefixMatchAlertVisible,
    closeAlertBar: closePrefixMatchAlertBar,
  } = useAlertVisible(prefixMatchedGenes);

  return (
    <>
      <div className="flex items-center justify-between" ref={contentRef}>
        <span className="text-[14px] font-normal leading-[22px] tracking-[-0.128px] text-3b-text-secondary">
          Result: {currentPageStartNumber}-{currentPageEndNumber}
          &nbsp;of&nbsp;
          {searchFilteredGeneData.length}
        </span>
        <DownloadButton
          title="Export detail (xls)"
          data={searchFilteredGeneData}
        />
      </div>

      <AlertBar
        {...getPrefixMatchAlertBarProps(searchedPrefixes, prefixMatchedGenes)}
        isVisible={isPrefixMatchAlertVisible}
        onClose={closePrefixMatchAlertBar}
      />

      <AlertBar
        {...getInvalidGeneAlertBarProps(searchedInvalidGenes)}
        isVisible={isInvalidGeneAlertVisible}
        onClose={closeInvalidGeneAlertBar}
      />

      <Table
        columnRenderInfoList={ColumnRenderInfoList}
        dataList={currentPageData}
        noResultLabel={<NoResultLabel />}
        className="overflow-x-auto"
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages === 0 ? 1 : totalPages}
        onPageClick={jump}
        onPrevClick={prev}
        onNextClick={next}
        className="my-4"
      />

      <Description />
    </>
  );
}

export default SearchResult;
